// src/pages/appointments/HospitalsPage.jsx
import React from "react";
import { useNavigate } from "react-router-dom";

import DoctorMap from "@/components/maps/DoctorMap.jsx";
import HospitalResultCard from "@/components/hospital/HospitalResultCard.jsx";
import { getAfriMedMapStyle } from "@/constants/mapStyles.js";

import SearchBar from "@/sot/primitives/SearchBar.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import useGeolocation from "@/hooks/useGeolocation.js";
import LocationConsentDialog from "@/components/location/LocationConsentDialog.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { SADC_MARKETS } from "@/config/markets.js";

import { useAppointmentsFilters } from "@/pages/appointments/AppointmentsFiltersContext.jsx";
import AppointmentsResultsRail from "@/pages/appointments/AppointmentsResultsRail.jsx";

// ✅ Your image pool (CSV has none → we rotate these safely)
const MOCK_HOSPITALS = [
  {
    id: "kalafhi",
    image: "/assets/images/hospital_filters/hospital-01.png",
  },
  {
    id: "sidilega",
    image: "/assets/images/hospital_filters/hospital-02.jpg",
  },
  {
    id: "riverside",
    image: "/assets/images/hospital_filters/hospital-03.jpg",
  },
  {
    id: "lenmed",
    image: "/assets/images/hospital_filters/hospital-04.jpg",
  },
  {
    id: "multicare",
    image: "/assets/images/hospital_filters/hospital-05.jpg",
  },
  {
    id: "santa-isabel",
    image: "/assets/images/hospital_filters/hospital.png",
  },
];

const DEMO_CENTER = { lat: -24.6282, lng: 25.9231 };
const EXTRA_SADC_COUNTRIES = ["Comoros", "Madagascar", "Mauritius", "Seychelles"];
const SADC_COUNTRY_ALIASES = {
  "dr congo": "dr congo",
  drc: "dr congo",
  "democratic republic of the congo": "dr congo",
  "congo kinshasa": "dr congo",
  swaziland: "eswatini",
};
const SEARCH_LOCATION_ALIASES = {
  capetown: "cape town",
  "cape-town": "cape town",
  jozi: "johannesburg",
  joburg: "johannesburg",
};
const SADC_COUNTRY_SET = new Set(
  [...SADC_MARKETS.map((market) => market.name), ...EXTRA_SADC_COUNTRIES].map((country) =>
    normalizeMatchText(country)
  )
);

const ICONS = {
  back: "/icons/filter-doctors/back-arrow.svg",
  cancel: "/icons/doctor/cancel.svg",
  filter: "/icons/filter-doctors/date_range_filled-dark.svg",
  nearby: "/icons/filter-doctors/distance.svg",
};

function normalizeMatchText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function normalizeFacilitySearchText(value = "") {
  return normalizeMatchText(value)
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function repairFacilityText(value = "") {
  return String(value || "")
    .replaceAll("├⌐", "é")
    .replaceAll("├¿", "è")
    .replaceAll("├┤", "ô")
    .replaceAll("├á", "á")
    .replaceAll("├à", "à")
    .replaceAll("ΓÇÖ", "'");
}

function toggleInArray(arr, id) {
  if (!Array.isArray(arr)) return [id];
  return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
}

/** minimal CSV split (quoted commas supported) */
function splitCsvLine(line = "") {
  const out = [];
  let cur = "";
  let q = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    // handle escaped quotes ""
    if (ch === '"' && line[i + 1] === '"') {
      cur += '"';
      i++;
      continue;
    }
    if (ch === '"') {
      q = !q;
      continue;
    }
    if (ch === "," && !q) {
      out.push(cur);
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur);

  return out.map((s) => String(s).trim());
}

function toNum(v) {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : null;
}

function haversineKm(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

function pick(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v != null && String(v).trim() !== "") return v;
  }
  return null;
}

function titleCase(s = "") {
  return repairFacilityText(s)
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

function imageForIndex(i) {
  const item = MOCK_HOSPITALS[i % MOCK_HOSPITALS.length];
  return item?.image || "/assets/images/hospital_filters/hospital.png";
}

function normalizeSadcCountry(country = "") {
  const normalized = normalizeMatchText(country);
  return SADC_COUNTRY_ALIASES[normalized] || normalized;
}

function isSadcCountry(country = "") {
  return SADC_COUNTRY_SET.has(normalizeSadcCountry(country));
}

function normalizeSearchLocation(value = "") {
  const normalized = normalizeMatchText(value);
  return SEARCH_LOCATION_ALIASES[normalized] || normalized;
}

function isHospitalFacility(row) {
  const facilityType = normalizeFacilitySearchText(row?.facility_type || row?.type || "");
  const facilityName = normalizeFacilitySearchText(
    row?.facility_name || row?.name || row?.Facility || row?.health_facility || ""
  );
  const haystack = `${facilityType} ${facilityName}`;

  return haystack.includes("hospital") || /\bh\s*pital\b/.test(haystack);
}

function formatDistance(distanceKm) {
  if (distanceKm == null || String(distanceKm).trim() === "") return null;
  const value = Number(distanceKm);
  if (!Number.isFinite(value)) return null;
  if (value < 1) return `${Math.max(1, Math.round(value * 1000))} m`;
  if (value < 10) return `${value.toFixed(1)} km`;
  return `${Math.round(value)} km`;
}

function inLngRange(lng, bounds) {
  if (!Number.isFinite(lng) || !bounds) return false;
  if (bounds.west <= bounds.east) return lng >= bounds.west && lng <= bounds.east;
  return lng >= bounds.west || lng <= bounds.east;
}

function facilityInBounds(facility, bounds) {
  if (!bounds) return true;
  const lat = Number(facility?.lat);
  const lng = Number(facility?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < bounds.south || lat > bounds.north) return false;
  return inLngRange(lng, bounds);
}

function boundsCenter(bounds) {
  if (!bounds) return null;
  const south = Number(bounds.south);
  const north = Number(bounds.north);
  const west = Number(bounds.west);
  const east = Number(bounds.east);
  if (![south, north, west, east].every(Number.isFinite)) return null;

  const lat = (south + north) / 2;
  let lng = (west + east) / 2;
  if (west > east) {
    lng = ((west + east + 360) / 2 + 540) % 360 - 180;
  }
  return { lat, lng };
}

function pickRatingValue(row) {
  const candidates = [
    row?.rating,
    row?.avg_rating,
    row?.average_rating,
    row?.rating_avg,
    row?.review_rating,
    row?.facility_rating,
  ];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) return value.toFixed(1);
  }

  return null;
}

export default function HospitalsPage() {
  const nav = useNavigate();
  const auth = useAuth();
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const { filters, setFilters, openSheet } = useAppointmentsFilters();
  const geo = useGeolocation();
  const mapboxToken = String(import.meta.env.VITE_MAPBOX_TOKEN || "").trim();
  const geocodeCacheRef = React.useRef(new Map());

  // responsive fork (same as DoctorsPage)
  const [isDesktop, setIsDesktop] = React.useState(() => {
    try {
      return window.matchMedia?.("(min-width: 768px)")?.matches ?? false;
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    let mql;
    try {
      mql = window.matchMedia("(min-width: 768px)");
      const onChange = () => setIsDesktop(!!mql.matches);
      onChange();
      mql.addEventListener?.("change", onChange);
      return () => mql.removeEventListener?.("change", onChange);
    } catch {
      // ignore
    }
  }, []);

  const [query, setQuery] = React.useState("");
  const [mapRef, setMapRef] = React.useState(null);

  const [loadingList, setLoadingList] = React.useState(false);
  const [error, setError] = React.useState("");

  const [facilities, setFacilities] = React.useState([]);
  const [highlightFacilityId, setHighlightFacilityId] = React.useState(null);
  const [selectedFacility, setSelectedFacility] = React.useState(null);
  const [queryFocus, setQueryFocus] = React.useState(null);
  const [visibleBounds, setVisibleBounds] = React.useState(null);
  const autoFitKeyRef = React.useRef("");

  const userCoords =
    geo?.coords?.lat != null &&
    geo?.coords?.lng != null
      ? { lat: Number(geo.coords.lat), lng: Number(geo.coords.lng) }
      : null;
  const canShowUser = !!userCoords;
  const patientCountry = normalizeSadcCountry(auth?.profile?.country || "");

  const geocodeSearchQuery = React.useCallback(
    async (queryString) => {
      const q = String(queryString || "").trim();
      if (!q || !mapboxToken) return null;

      const cacheKey = q.toLowerCase();
      if (geocodeCacheRef.current.has(cacheKey)) {
        return geocodeCacheRef.current.get(cacheKey);
      }

      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json` +
        `?access_token=${encodeURIComponent(mapboxToken)}&limit=1&types=place,locality,neighborhood,address`;

      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const json = await res.json();
        const center = json?.features?.[0]?.center;
        if (!Array.isArray(center) || center.length < 2) return null;
        const coords = { lng: Number(center[0]), lat: Number(center[1]) };
        if (!Number.isFinite(coords.lng) || !Number.isFinite(coords.lat)) return null;
        geocodeCacheRef.current.set(cacheKey, coords);
        return coords;
      } catch {
        return null;
      }
    },
    [mapboxToken]
  );

  React.useEffect(() => {
    let cancelled = false;
    const q = normalizeSearchLocation(query);
    if (!q) {
      setQueryFocus(null);
      return undefined;
    }

    const normalizedQuery = normalizeSadcCountry(q);
    const hasExactCountryMatch = facilities.some(
      (facility) => normalizeSadcCountry(facility.country || "") === normalizedQuery
    );
    const hasExactProvinceMatch = facilities.some(
      (facility) => normalizeMatchText(facility.province || "") === normalizeMatchText(q)
    );

    if (hasExactCountryMatch || hasExactProvinceMatch) {
      setQueryFocus(null);
      return undefined;
    }

    const run = async () => {
      const coords = await geocodeSearchQuery(q);
      if (!cancelled) {
        setQueryFocus(coords);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [facilities, geocodeSearchQuery, query]);

  // ✅ Load facilities once
  React.useEffect(() => {
    let alive = true;

    async function loadFacilities() {
      setLoadingList(true);
      setError("");

      try {
        const res = await fetch("/data/health_facilities_sadc.csv", { cache: "no-store" });
        if (!res.ok) throw new Error("Failed to load facilities CSV");

        const text = await res.text();
        const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        if (lines.length < 2) throw new Error("Facilities CSV is empty");

        const headers = splitCsvLine(lines[0]);
        const rows = lines.slice(1).map((l) => {
          const cols = splitCsvLine(l);
          const obj = {};
          headers.forEach((h, i) => (obj[h] = cols[i]));
          return obj;
        });

        const objs = rows
          .filter((row) => {
            const country = pick(row, ["Country", "country"]);
            return isHospitalFacility(row) && isSadcCountry(country);
          })
          .map((r, idx) => {
            const latRaw = pick(r, ["lat", "latitude", "Latitude", "LAT", "y", "Y"]);
            const lngRaw = pick(r, ["lng", "lon", "longitude", "Longitude", "LON", "x", "X"]);
            const lat = toNum(latRaw);
            const lng = toNum(lngRaw);
            if (lat == null || lng == null) return null;
            if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return null;

            const name =
              pick(r, ["name", "Name", "facility_name", "Facility", "facility", "health_facility"]) ||
              "Hospital";

            const addressParts = [
              pick(r, ["address", "Address", "facility_address", "location", "Location", "town", "province"]),
              pick(r, ["Country", "country"]),
            ]
              .filter(Boolean)
              .map((part) => repairFacilityText(part).trim())
              .filter(Boolean);

            return {
              id: `fac-${idx}`,
              name: repairFacilityText(name).trim(),
              address: addressParts.join(", "),
              lat,
              lng,
              country: String(pick(r, ["Country", "country"]) || "").trim(),
              province: String(pick(r, ["province", "Province"]) || "").trim(),
            };
          })
          .filter(Boolean);

        if (!alive) return;
        setFacilities(objs);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Failed to load hospitals");
        setFacilities([]);
      } finally {
        if (!alive) return;
        setLoadingList(false);
      }
    }

    loadFacilities();
    return () => {
      alive = false;
    };
  }, []);

  const activeFilterCount =
    (filters.data?.length || 0) +
    (filters.specialty?.length || 0) +
    (filters.payment?.length || 0) +
    (filters.language?.length || 0) +
    (filters.date ? 1 : 0);

  // filter list (query + nearby)
  const filtered = React.useMemo(() => {
    const q = normalizeSearchLocation(query);
    const normalizedQuery = normalizeSadcCountry(q);
    let list = Array.isArray(facilities) ? facilities : [];
    let hasQueryFocusOrdering = false;
    let hasExplicitSearch = false;

    if (q) {
      hasExplicitSearch = true;
      const exactCountryMatches = list.filter(
        (facility) => normalizeSadcCountry(facility.country || "") === normalizedQuery
      );
      if (exactCountryMatches.length > 0) {
        list = exactCountryMatches;
      } else {
        const exactProvinceMatches = list.filter(
          (facility) => normalizeMatchText(facility.province || "") === normalizeMatchText(q)
        );
        if (exactProvinceMatches.length > 0) {
          list = exactProvinceMatches;
        } else if (
          queryFocus &&
          Number.isFinite(Number(queryFocus.lat)) &&
          Number.isFinite(Number(queryFocus.lng))
        ) {
          list = list
            .map((facility) => ({
              ...facility,
              queryDistanceKm: haversineKm(queryFocus, {
                lat: facility.lat,
                lng: facility.lng,
              }),
            }))
            .sort((a, b) => a.queryDistanceKm - b.queryDistanceKm || a.name.localeCompare(b.name))
            .slice(0, 200);
          hasQueryFocusOrdering = true;
        } else {
          list = list.filter((f) => {
            const haystack = normalizeMatchText(
              `${f.name} ${f.address} ${f.country} ${f.province}`
            );
            return haystack.includes(q);
          });
        }
      }
    }

    if (!q && !userCoords && patientCountry) {
      const sameCountry = list.filter(
        (facility) => normalizeSadcCountry(facility.country || "") === patientCountry
      );
      if (sameCountry.length > 0) {
        list = sameCountry;
      }
    }

    list = list.map((facility) => ({
      ...facility,
      distanceKm: userCoords
        ? haversineKm(userCoords, { lat: facility.lat, lng: facility.lng })
        : null,
    }));

    if (
      q &&
      queryFocus &&
      Number.isFinite(Number(queryFocus.lat)) &&
      Number.isFinite(Number(queryFocus.lng))
    ) {
      list = list.map((facility) => ({
        ...facility,
        queryDistanceKm:
          facility.queryDistanceKm ??
          haversineKm(queryFocus, { lat: facility.lat, lng: facility.lng }),
      }));
      hasQueryFocusOrdering = true;
    }

    if (hasQueryFocusOrdering) {
      list = list.sort(
        (a, b) =>
          Number(a.queryDistanceKm ?? Number.POSITIVE_INFINITY) -
            Number(b.queryDistanceKm ?? Number.POSITIVE_INFINITY) ||
          String(a.country || "").localeCompare(String(b.country || "")) ||
          String(a.province || "").localeCompare(String(b.province || "")) ||
          a.name.localeCompare(b.name)
      );
    } else if (userCoords && !hasExplicitSearch) {
      list = list.sort((a, b) => a.distanceKm - b.distanceKm || a.name.localeCompare(b.name));
    } else if (!q && !patientCountry) {
      list = list
        .map((facility) => ({
          ...facility,
          fallbackDistanceKm: haversineKm(DEMO_CENTER, {
            lat: facility.lat,
            lng: facility.lng,
          }),
        }))
        .sort(
          (a, b) =>
            Number(a.fallbackDistanceKm ?? Number.POSITIVE_INFINITY) -
              Number(b.fallbackDistanceKm ?? Number.POSITIVE_INFINITY) ||
            String(a.country || "").localeCompare(String(b.country || "")) ||
            String(a.province || "").localeCompare(String(b.province || "")) ||
            a.name.localeCompare(b.name)
        );
    } else {
      list = list.sort(
        (a, b) =>
          String(a.country || "").localeCompare(String(b.country || "")) ||
          String(a.province || "").localeCompare(String(b.province || "")) ||
          a.name.localeCompare(b.name)
      );
    }

    if (filters.data?.includes("near-you") && userCoords) {
      list = list.filter((f) => f.distanceKm <= 35);
    }

    return list.slice(0, 200);
  }, [facilities, query, filters.data, patientCountry, queryFocus, userCoords]);

  const mapCandidates = React.useMemo(() => {
    const q = normalizeSearchLocation(query);
    const normalizedQuery = normalizeSadcCountry(q);
    let list = Array.isArray(facilities) ? facilities : [];
    let hasQueryFocusOrdering = false;

    if (q) {
      const exactCountryMatches = list.filter(
        (facility) => normalizeSadcCountry(facility.country || "") === normalizedQuery
      );
      if (exactCountryMatches.length > 0) {
        list = exactCountryMatches;
      } else {
        const exactProvinceMatches = list.filter(
          (facility) => normalizeMatchText(facility.province || "") === normalizeMatchText(q)
        );
        if (exactProvinceMatches.length > 0) {
          list = exactProvinceMatches;
        } else if (
          queryFocus &&
          Number.isFinite(Number(queryFocus.lat)) &&
          Number.isFinite(Number(queryFocus.lng))
        ) {
          list = list
            .map((facility) => ({
              ...facility,
              queryDistanceKm: haversineKm(queryFocus, {
                lat: facility.lat,
                lng: facility.lng,
              }),
            }))
            .sort((a, b) => a.queryDistanceKm - b.queryDistanceKm || a.name.localeCompare(b.name));
          hasQueryFocusOrdering = true;
        } else {
          list = list.filter((f) => {
            const haystack = normalizeMatchText(
              `${f.name} ${f.address} ${f.country} ${f.province}`
            );
            return haystack.includes(q);
          });
        }
      }
    }

    list = list.map((facility) => ({
      ...facility,
      distanceKm: userCoords
        ? haversineKm(userCoords, { lat: facility.lat, lng: facility.lng })
        : null,
    }));

    if (
      q &&
      queryFocus &&
      Number.isFinite(Number(queryFocus.lat)) &&
      Number.isFinite(Number(queryFocus.lng))
    ) {
      list = list.map((facility) => ({
        ...facility,
        queryDistanceKm:
          facility.queryDistanceKm ??
          haversineKm(queryFocus, { lat: facility.lat, lng: facility.lng }),
      }));
      hasQueryFocusOrdering = true;
    }

    if (filters.data?.includes("near-you") && userCoords) {
      list = list.filter((f) => f.distanceKm <= 35);
    }

    if (hasQueryFocusOrdering) {
      return list.sort(
        (a, b) =>
          Number(a.queryDistanceKm ?? Number.POSITIVE_INFINITY) -
            Number(b.queryDistanceKm ?? Number.POSITIVE_INFINITY) ||
          String(a.country || "").localeCompare(String(b.country || "")) ||
          String(a.province || "").localeCompare(String(b.province || "")) ||
          a.name.localeCompare(b.name)
      );
    }

    return list.sort(
      (a, b) =>
        String(a.country || "").localeCompare(String(b.country || "")) ||
        String(a.province || "").localeCompare(String(b.province || "")) ||
        a.name.localeCompare(b.name)
    );
  }, [facilities, filters.data, query, queryFocus, userCoords]);

  const visibleResults = React.useMemo(() => {
    if (!visibleBounds) return filtered;

    const center = boundsCenter(visibleBounds);
    const list = mapCandidates.filter((facility) => facilityInBounds(facility, visibleBounds));
    if (center) {
      return list
        .map((facility) => ({
          ...facility,
          visibleDistanceKm: haversineKm(center, {
            lat: facility.lat,
            lng: facility.lng,
          }),
        }))
        .sort(
          (a, b) =>
            Number(a.visibleDistanceKm ?? Number.POSITIVE_INFINITY) -
              Number(b.visibleDistanceKm ?? Number.POSITIVE_INFINITY) ||
            String(a.country || "").localeCompare(String(b.country || "")) ||
            String(a.province || "").localeCompare(String(b.province || "")) ||
            a.name.localeCompare(b.name)
        )
        .slice(0, 200);
    }

    return list.slice(0, 200);
  }, [filtered, mapCandidates, visibleBounds]);

  React.useEffect(() => {
    if (geo?.consent === "always" && geo?.status === "granted") {
      geo.startWatch();
      return () => geo.stopWatch();
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo?.consent, geo?.status]);

  const handleNearMe = React.useCallback(async () => {
    if (userCoords && mapRef) {
      mapRef.flyTo({
        center: [Number(userCoords.lng), Number(userCoords.lat)],
        zoom: 13,
        duration: 900,
      });
      return;
    }

    if (geo.needsConsent) {
      geo.openDialog();
      return;
    }

    if (geo.status === "denied" || geo.consent === "denied") return;
    geo.getOnce();
  }, [geo, mapRef, userCoords]);

  // card model (HospitalResultCard expects image/distance/rating etc.)
  const cards = React.useMemo(() => {
    return visibleResults.map((f, i) => ({
      id: f.id,
      name: titleCase(f.name),
      address: titleCase(f.address || ""),
      image: imageForIndex(i),
      distance: formatDistance(f.distanceKm),
      rating: pickRatingValue(f),
      lat: f.lat,
      lng: f.lng,
    }));
  }, [visibleResults]);

  const mapCenter = React.useMemo(() => {
    if (
      selectedFacility &&
      Number.isFinite(Number(selectedFacility.lat)) &&
      Number.isFinite(Number(selectedFacility.lng))
    ) {
      return { lat: Number(selectedFacility.lat), lng: Number(selectedFacility.lng) };
    }

    if (
      queryFocus &&
      Number.isFinite(Number(queryFocus.lat)) &&
      Number.isFinite(Number(queryFocus.lng))
    ) {
      return { lat: Number(queryFocus.lat), lng: Number(queryFocus.lng) };
    }

    if (userCoords) return userCoords;

    const firstVisible = filtered.find(
      (facility) =>
        Number.isFinite(Number(facility?.lat)) && Number.isFinite(Number(facility?.lng))
    );
    if (firstVisible) {
      return { lat: Number(firstVisible.lat), lng: Number(firstVisible.lng) };
    }

    return DEMO_CENTER;
  }, [filtered, queryFocus, selectedFacility, userCoords]);
  

 // ✅ click card => pan/zoom + highlight marker
  const fitHospitalsToMap = React.useCallback(
    (items, { includeUser = false, force = false, sampleCount = 8, anchor = null } = {}) => {
      const mapLike = mapRef;
      if (!mapLike || !Array.isArray(items) || items.length === 0) return;

      const mapInstance =
        typeof mapLike.fitBounds === "function"
          ? mapLike
          : typeof mapLike.getMap === "function"
            ? mapLike.getMap()
            : null;
      if (!mapInstance || typeof mapInstance.fitBounds !== "function") return;

      const points = items
        .slice(0, sampleCount)
        .map((item) => [Number(item.lng), Number(item.lat)])
        .filter(([lng, lat]) => Number.isFinite(lat) && Number.isFinite(lng));

      if (includeUser && userCoords) {
        points.push([Number(userCoords.lng), Number(userCoords.lat)]);
      }

      if (
        anchor &&
        Number.isFinite(Number(anchor.lng)) &&
        Number.isFinite(Number(anchor.lat))
      ) {
        points.push([Number(anchor.lng), Number(anchor.lat)]);
      }

      if (points.length === 0) return;

      const key = JSON.stringify(points);
      if (!force && autoFitKeyRef.current === key) return;
      autoFitKeyRef.current = key;

      if (points.length === 1) {
        mapInstance.flyTo({
          center: points[0],
          zoom: includeUser && userCoords ? 12.5 : 13.5,
          duration: 900,
        });
        return;
      }

      let minLng = points[0][0];
      let maxLng = points[0][0];
      let minLat = points[0][1];
      let maxLat = points[0][1];

      points.forEach(([lng, lat]) => {
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      });

      mapInstance.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: {
            top: 120,
            right: 48,
            bottom: 280,
            left: 48,
          },
          duration: 900,
          maxZoom: 13.5,
        }
      );
    },
    [mapRef, userCoords]
  );

  React.useEffect(() => {
    if (!filtered.length || !mapRef || selectedFacility) return;
    fitHospitalsToMap(filtered, {
      includeUser: !!userCoords && !normalizeSearchLocation(query),
      force: !!normalizeSearchLocation(query),
      sampleCount: normalizeSearchLocation(query) ? Math.min(filtered.length, 32) : 8,
      anchor:
        normalizeSearchLocation(query) &&
        queryFocus &&
        Number.isFinite(Number(queryFocus.lat)) &&
        Number.isFinite(Number(queryFocus.lng))
          ? { lat: Number(queryFocus.lat), lng: Number(queryFocus.lng) }
          : null,
    });
  }, [filtered, fitHospitalsToMap, mapRef, query, queryFocus, selectedFacility, userCoords]);

  React.useEffect(() => {
    if (!selectedFacility) return;
    const stillVisible = visibleResults.some((facility) => facility.id === selectedFacility.id);
    if (!stillVisible) {
      setSelectedFacility(null);
      setHighlightFacilityId(null);
    }
  }, [selectedFacility, visibleResults]);

  React.useEffect(() => {
    if (!normalizeSearchLocation(query)) return;
    setSelectedFacility(null);
    setHighlightFacilityId(null);
  }, [query]);

  const locateHospital = React.useCallback(
    (card) => {
      if (!mapRef) return;

      const lat = card?.lat;
      const lng = card?.lng ?? card?.lon;

      if (lat == null || lng == null) return;

      setSelectedFacility(card);
      setHighlightFacilityId(card.id);
      window.setTimeout(() => setHighlightFacilityId(null), 1200);

      mapRef.flyTo({
        center: [Number(lng), Number(lat)],
        zoom: 15,
        duration: 900,
      });
    },
    [mapRef]
  );

  const handleFacilitySelect = React.useCallback((facility) => {
    if (!facility) return;
    setSelectedFacility(facility);
    setHighlightFacilityId(facility.id || null);
    window.setTimeout(() => setHighlightFacilityId(null), 1200);
  }, []);

  return (
    <div>
      {/* FULL MAP FLOOR */}
      <div className="fixed inset-0">
        <DoctorMap
          diagnosticSurface="hospitals-discovery"
          center={mapCenter}
          showUser={canShowUser}
          user={userCoords}
          doctors={[]}
          facilities={mapCandidates}
          heightClass="h-full"
          frameClassName="w-full h-full"
          mapStyle={getAfriMedMapStyle(isDark)}
          facilityIcon="/markers/afrimed-hospital.svg"
          userIcon="/markers/afrimed-user.svg"
          highlightFacilityId={highlightFacilityId}
          focusTarget={selectedFacility}
          onFacilitySelect={handleFacilitySelect}
          onFacilityPopupClose={() => setSelectedFacility(null)}
          onRequireLocation={handleNearMe}
          onMapReady={(m) => setMapRef(m)}
          onBoundsChange={setVisibleBounds}
        />
      </div>

      {/* TOP OVERLAYS */}
      <div className="fixed inset-x-0 top-0 md:top-14 z-30">
        <div className="w-full max-w-[720px] mx-auto px-4 pt-3 md:pt-4">
          <SearchBar
            value={query}
            onChange={setQuery}
            placeholder={t("appointments.directory.hospitals.search", "Search hospitals")}
            leftIconSrc={ICONS.back}
            onLeftIconClick={() => nav(-1)}
            rightIconSrc={query?.trim() ? ICONS.cancel : null}
            onRightIconClick={() => setQuery("")}
            variant="solid"
          />

          <div className="chips-viewport mt-3 no-scrollbar">
            <div className="chips-row">
              <AfriPill
                label={t("appointments.directory.hospitals.nearby", "Nearby")}
                iconLeft={ICONS.nearby}
                isActive={filters.data?.includes("near-you")}
                variant="doctor"
                value="near-you"
                onChange={(v) =>
                  setFilters((prev) => ({
                    ...prev,
                    data: toggleInArray(prev.data, v),
                  }))
                }
              />

              <AfriPill
                label={`${t("appointments.directory.common.filter", "Filter")}${activeFilterCount ? ` · ${activeFilterCount}` : ""}`}
                iconLeft={ICONS.filter}
                isActive={activeFilterCount > 0}
                variant="doctor"
                value="main"
                onChange={openSheet}
              />
            </div>
          </div>
        </div>
      </div>

      {/* RESULTS PANEL */}
      <div className="fixed inset-x-0 bottom-0 z-20">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-[calc(128px+env(safe-area-inset-bottom))] md:pb-24">
          <div className="relative">
            {loadingList && (
              <div className="text-body-medium text-on-surface-var px-1 pb-2">
                {t("appointments.directory.common.loading", "Loading...")}
              </div>
            )}

            {!loadingList && error && (
              <div className="text-body-medium text-[crimson] px-1 pb-2">{error}</div>
            )}

            {!loadingList && !error && cards.length === 0 && (
              <div className="text-body-medium text-on-surface-var mt-2">
                {visibleBounds
                  ? t(
                      "appointments.directory.hospitals.emptyMapArea",
                      "No hospitals found in this map area. Move the map or reset the view."
                    )
                  : t("appointments.directory.hospitals.empty", "No hospitals found.")}
              </div>
            )}

            <AppointmentsResultsRail
              items={cards}
              onSelect={(h) => locateHospital(h)}
              renderCard={(h) => <HospitalResultCard hospital={h} onLocate={locateHospital} />}
            />

            {/* Near-me button */}
            <button
              type="button"
              onClick={handleNearMe}
              className="
                absolute right-4 -top-14 h-11 w-11 rounded-[16px]
                bg-[rgb(var(--clr-surface-cont-highest))]
                flex items-center justify-center
                shadow-[0_10px_30px_rgba(15,23,42,0.18)]
              "
              aria-label={t("appointments.directory.common.centerMap", "Center map")}
            >
              <img
                src="/icons/doctor/near_me_filled.svg"
                alt=""
                className="h-5 w-5"
                draggable="false"
              />
            </button>
          </div>
        </div>
      </div>

      <LocationConsentDialog
        open={geo.dialogOpen}
        onClose={geo.closeDialog}
        onAllowOnce={geo.allowOnce}
        onAllowAlways={geo.allowAlways}
        onDeny={geo.deny}
      />
    </div>
  );
}
